import { DashboardLayout } from "../../components/dashboard-layout";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { theme } from "../../theme"; 
import { 
    Box, Button, Container, Table, TableBody, TableCell, TableHead, TableRow, 
    TextField, Card, CardContent, Typography, Stack, Chip, TableContainer, 
    CircularProgress, IconButton, Grid, Tabs, Tab
} from "@mui/material";
import { 
    CampaignOutlined, AddCircleOutline, EditOutlined, 
    VisibilityOutlined, VisibilityOffOutlined, DeleteOutline, FolderOpenOutlined
} from "@mui/icons-material";
import axios from "axios";
import swal from "sweetalert";
import { t } from "i18next";
import React, { useEffect, useState, useRef, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const metronicTheme = createTheme({
    palette: {
        background: { default: "#F5F8FA", paper: "#FFFFFF" }, 
        text: { primary: "#181C32", secondary: "#7E8299" }, 
        primary: { main: "#1B84FF", light: "#F1FAFF" },
        success: { main: "#50CD89", light: "#E8FFF3" },
        danger: { main: "#F1416C", light: "#FFF5F8" },
        warning: { main: "#FFC700", light: "#FFF8DD" },
    },
    typography: { fontFamily: "'Plus Jakarta Sans', sans-serif" },
    components: {
        MuiCard: { styleOverrides: { root: { borderRadius: "12px", boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.03)", border: "1px solid #E4E6EF" } } },
        MuiTableCell: { styleOverrides: { root: { borderBottom: "1px solid #EFF2F5 !important", padding: "14px 12px", color: "#3F4254", fontSize: "0.85rem", fontFamily: "'Plus Jakarta Sans', sans-serif !important" } } },
        MuiButton: { styleOverrides: { root: { fontFamily: "'Plus Jakarta Sans', sans-serif", textTransform: 'none' } } }
    }
});

const formats = ["header", "bold", "italic", "underline", "strike", "blockquote", "list", "bullet", "indent", "link", "image"];

axios.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("user");
        if (token) { config.headers["Authorization"] = `Bearer ${token}`; }
        return config;
    },
    (error) => Promise.reject(error)
);

function SettingNotify() {
    const [notify, setNotify] = useState();
    const [categories, setCategories] = useState([]); 
    const [load, setLoad] = useState(true);
    
    // States cho Tab ở Bảng Danh Sách
    const [activeTab, setActiveTab] = useState("promo"); // Mặc định là 'promo'

    // States cho Form Thêm Mới
    const [displayLocation, setDisplayLocation] = useState("promo"); 
    const [content, setContent] = useState("");

    // States cho Modal Sửa Thông Báo
    const [isShow, setShow] = useState(false);
    const [notifyItem, setNotifyItem] = useState();
    const [selectedEditCategory, setSelectedEditCategory] = useState("");
    const [editDisplayLocation, setEditDisplayLocation] = useState("promo");
    
    // States cho Modal Danh Mục
    const [showAddCatModal, setShowAddCatModal] = useState(false); 
    const [showEditCatModal, setShowEditCatModal] = useState(false);
    const [editCatData, setEditCatData] = useState({ id: "", name: "", stt: 0 });

    const handleChangeContent = (value) => { setContent(value); };
    
    // ==========================================
    // 1. FETCH DỮ LIỆU
    // ==========================================
    useEffect(() => {
        if (load === true) {
            axios.get(`${process.env.REACT_APP_API_URL}/auth/getnotify`).then((res) => {
                setNotify(res.data.data);
            }).catch(() => {});

            axios.get(`${process.env.REACT_APP_API_URL}/category/get`).then((res) => {
                if (res.data && res.data.data) { setCategories(res.data.data); }
                setLoad(false);
            }).catch(() => setLoad(false));
        }
    }, [load]);

    // ==========================================
    // 2. LOGIC XỬ LÝ DANH MỤC
    // ==========================================
    const handleAddCategory = (e) => {
        e.preventDefault();
        setLoad(true);
        const formData = { name: e.target.catName.value, stt: e.target.catStt.value || 0 };
        axios.post(`${process.env.REACT_APP_API_URL}/category/create`, formData)
            .then(() => {
                swal("Thành công", "Đã thêm danh mục mới!", "success");
                setShowAddCatModal(false);
                setLoad(true);
            }).catch(() => {
                swal("Lỗi", "Không thể thêm danh mục", "error");
                setLoad(false);
            });
    };

    const handleEditCategorySubmit = (e) => {
        e.preventDefault();
        setLoad(true);
        const formData = { id: editCatData.id, name: e.target.catName.value, stt: e.target.catStt.value };
        axios.post(`${process.env.REACT_APP_API_URL}/category/edit`, formData)
            .then(() => {
                swal("Thành công", "Cập nhật danh mục thành công!", "success");
                setShowEditCatModal(false);
                setLoad(true);
            }).catch(() => {
                swal("Lỗi", "Có lỗi xảy ra", "error");
                setLoad(false);
            });
    };

    const handleDeleteCategory = (id) => {
        swal({ title: "Bạn chắc chắn?", text: "Danh mục này sẽ bị xóa vĩnh viễn!", icon: "warning", buttons: ["Hủy", "Xóa"], dangerMode: true })
        .then((willDelete) => {
            if (willDelete) {
                setLoad(true);
                axios.post(`${process.env.REACT_APP_API_URL}/category/delete`, { id: id })
                    .then(() => { swal("Đã xóa!", "Danh mục đã bị xóa", "success").then(() => setLoad(true)); })
                    .catch(() => { swal("Lỗi!", "Có lỗi xảy ra", "error"); setLoad(false); });
            }
        });
    };

    // ==========================================
    // 3. LOGIC XỬ LÝ THÔNG BÁO / BANNER
    // ==========================================
    const handleSubmit = (e) => {
        e.preventDefault();
        setLoad(true);
        
        const isHome = displayLocation === 'home';
        
        const formData = {
            displayLocation: displayLocation,
            stt: e.target.stt.value, 
            image: e.target.image.value,
            category: isHome ? "Banner" : (e.target.category ? e.target.category.value : "Chưa phân loại"),
            title: isHome ? "Banner Trang Chủ" : (e.target.title ? e.target.title.value : "Banner"),
            content: isHome ? "<p>Ảnh Banner Trang Chủ</p>" : content
        };
        
        axios.post(`${process.env.REACT_APP_API_URL}/auth/newnotify`, formData)
            .then(() => { swal(t("success"), "Thêm mới thành công!", "success").then(() => window.location.reload()); })
            .catch(() => setLoad(false));
    };

    const handleSubmitnotify = (e) => {
        e.preventDefault();
        setLoad(true);
        
        const isHome = editDisplayLocation === 'home';
        
        const formData = {
            id: notifyItem._id,
            displayLocation: editDisplayLocation,
            stt: e.target.stt ? e.target.stt.value : notifyItem.stt, 
            image: e.target.image ? e.target.image.value : notifyItem.image,
            category: isHome ? "Banner" : selectedEditCategory,
            title: isHome ? "Banner Trang Chủ" : (e.target.title ? e.target.title.value : notifyItem.title),
            content: isHome ? "<p>Ảnh Banner Trang Chủ</p>" : content
        };
        
        axios.post(`${process.env.REACT_APP_API_URL}/auth/editlistnotify`, formData).then(() => {
            swal(t("success"), "Cập nhật thành công!", "success").then(() => window.location.reload());
            setShow(false);
        }).catch(() => { swal(t("error"), "Có lỗi xảy ra", "error"); setLoad(false); });
    };

    const handleDelete = (id) => {
        swal({ title: "Bạn có chắc chắn?", text: "Khi xóa, thông báo này sẽ không thể khôi phục!", icon: "warning", buttons: ["Hủy", "Xóa"], dangerMode: true })
        .then((willDelete) => {
            if (willDelete) {
                setLoad(true);
                axios.post(`${process.env.REACT_APP_API_URL}/auth/deletenotify`, { id: id })
                    .then(() => { swal("Thành công!", "Đã xóa thông báo.", "success").then(() => setLoad(true)); })
                    .catch(() => { swal("Lỗi!", "Có lỗi xảy ra khi xóa", "error"); setLoad(false); });
            }
        });
    };

    // Tìm kiếm trực tiếp phần tử marquee trong danh sách
    const marqueeItem = notify?.find(item => item.title === "marquee");

    // Hàm Lọc dữ liệu thông báo cho bảng dựa theo activeTab
    const getFilteredNotify = () => {
        if (!notify) return [];
        return notify.filter(item => {
            if (item.title === "marquee") return false; // Không hiển thị Marquee trong bảng
            const loc = item.displayLocation || 'promo';
            return loc === activeTab;
        });
    };
    const filteredNotify = getFilteredNotify();

    const reactQuillRef = useRef(null);
    const uploadImage = async (file) => {
        const formData = new FormData();
        formData.append("image", file);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/upload`, { method: "POST", body: formData });
        const data = await response.json();
        return data.url;
    };

    const modules = useMemo(() => ({
        toolbar: {
            container: [
                [{ header: [1, 2, false] }],
                ["bold", "italic", "underline", "strike", "blockquote"],
                [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
                ["link", "image"],
                ["clean"]
            ]
        }
    }), []);

    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px', backgroundColor: '#F9F9F9', height: '46px', 
            '& fieldset': { borderColor: '#E4E6EF' },
            '&:hover fieldset': { borderColor: '#B5B5C3' },
            '&.Mui-focused fieldset': { borderColor: '#1B84FF', borderWidth: '1px' },
        },
        '& .MuiInputBase-input': { height: '46px', padding: '0 14px', boxSizing: 'border-box', fontSize: '0.9rem', color: '#181C32', fontWeight: 600 }
    };
    const tableHeadSx = { color: "#A1A5B7 !important", fontWeight: "800 !important", fontSize: "0.75rem !important", backgroundColor: "#FFFFFF !important", borderBottom: "2px solid #EFF2F5 !important", textTransform: "uppercase !important" };

    return (
        <ThemeProvider theme={theme}>
            <DashboardLayout>
                <ThemeProvider theme={metronicTheme}>
                    <style>{`
                        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
                        .safe-notify-zone * { font-family: 'Plus Jakarta Sans', sans-serif !important; }
                        .ql-container.ql-snow { border: 1px solid #E4E6EF !important; border-bottom-left-radius: 8px; border-bottom-right-radius: 8px; font-size: 0.95rem; min-height: 150px; }
                        .ql-toolbar.ql-snow { border: 1px solid #E4E6EF !important; border-top-left-radius: 8px; border-top-right-radius: 8px; background-color: #F9F9F9; border-bottom: none !important; }
                        .ql-editor { color: #181C32; font-weight: 500; }
                    `}</style>

                    <Box className="safe-notify-zone" component="main" sx={{ flexGrow: 1, py: 4, backgroundColor: 'background.default', minHeight: 'calc(100vh - 70px)', position: 'relative' }}>
                        {load && (
                            <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' }}>
                                <CircularProgress size={50} sx={{ color: '#1B84FF' }} />
                            </Box>
                        )}

                        <Container maxWidth="xl">
                            <Box mb={4} display="flex" alignItems="center" gap={1.5}>
                                <CampaignOutlined sx={{ fontSize: '2.2rem', color: '#1B84FF' }} />
                                <Box>
                                    <Typography variant="h4" fontWeight="800" color="#181C32">Cài Đặt Hệ Thống</Typography>
                                    <Typography variant="body2" color="text.secondary">Quản lý danh mục, banner trang chủ và thông báo khuyến mãi</Typography>
                                </Box>
                            </Box>

                            {/* DÒNG CHỮ CHẠY (MARQUEE) - Đã sửa lỗi lấy đúng giá trị marquee */}
                            {marqueeItem && (
                                <Card sx={{ mb: 4, backgroundColor: '#FFF5F8', border: '1px solid #FCA5A5' }}>
                                    <CardContent sx={{ p: '16px !important', display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Chip label="MARQUEE" color="error" size="small" sx={{ fontWeight: 800, borderRadius: '4px' }} />
                                        <Box sx={{ flex: 1, overflow: 'hidden', color: '#F1416C', fontWeight: 700 }}>
                                            <marquee><div dangerouslySetInnerHTML={{ __html: marqueeItem.content }} style={{ display: 'inline' }} /></marquee>
                                        </Box>
                                        <Button variant="contained" color="error" size="small" 
                                            onClick={() => { 
                                                setNotifyItem(marqueeItem); 
                                                setContent(marqueeItem.content); 
                                                setSelectedEditCategory(marqueeItem.category || "");
                                                setEditDisplayLocation(marqueeItem.displayLocation || "promo");
                                                setShow(true); 
                                            }}
                                            sx={{ fontWeight: 800, boxShadow: 'none', whiteSpace: 'nowrap' }}>
                                            Sửa Chữ Chạy
                                        </Button>
                                    </CardContent>
                                </Card>
                            )}

                            {/* ===================================== */}
                            {/* KHU VỰC 1: QUẢN LÝ DANH MỤC */}
                            {/* ===================================== */}
                            <Card sx={{ mb: 4, backgroundColor: '#FFFFFF' }}>
                                <CardContent sx={{ p: 4, pb: '16px !important' }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="subtitle1" fontWeight="800" color="#181C32" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <FolderOpenOutlined color="primary" /> QUẢN LÝ DANH MỤC KHUYẾN MÃI
                                        </Typography>
                                        <Button variant="contained" color="primary" size="small" startIcon={<AddCircleOutline/>} onClick={() => setShowAddCatModal(true)} sx={{ fontWeight: 800, borderRadius: '6px', boxShadow: 'none' }}>
                                            Thêm Danh Mục Mới
                                        </Button>
                                    </Box>
                                    
                                    <TableContainer sx={{ border: '1px solid #EFF2F5', borderRadius: '8px' }}>
                                        <Table sx={{ minWidth: 1460 }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{...tableHeadSx, pl: 3, width: '10%'}}>STT</TableCell>
                                                    <TableCell sx={tableHeadSx}>TÊN DANH MỤC</TableCell>
                                                    <TableCell align="right" sx={{...tableHeadSx, pr: 3, width: '20%'}}>THAO TÁC</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {categories.length > 0 ? (
                                                    categories.map((cat) => (
                                                        <TableRow key={cat._id} hover>
                                                            <TableCell sx={{ pl: 3 }}>
                                                                <Typography variant="body2" fontWeight="800" color="#181C32">{cat.stt}</Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" fontWeight="700" color="#1B84FF">{cat.name}</Typography>
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ pr: 3 }}>
                                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                                    <IconButton size="small" 
                                                                        onClick={() => { setEditCatData({ id: cat._id, name: cat.name, stt: cat.stt }); setShowEditCatModal(true); }}
                                                                        sx={{ color: '#1B84FF', backgroundColor: '#F1FAFF', borderRadius: '6px', '&:hover': { backgroundColor: '#1B84FF', color: '#FFF' } }}>
                                                                        <EditOutlined fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton size="small" 
                                                                        onClick={() => handleDeleteCategory(cat._id)}
                                                                        sx={{ color: '#F1416C', backgroundColor: '#FFF5F8', borderRadius: '6px', '&:hover': { backgroundColor: '#F1416C', color: '#FFF' } }}>
                                                                        <DeleteOutline fontSize="small" />
                                                                    </IconButton>
                                                                </Stack>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3, color: '#A1A5B7', fontWeight: 700 }}>Chưa có danh mục nào.</TableCell></TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Card>

                            {/* ===================================== */}
                            {/* KHU VỰC 2: QUẢN LÝ THÔNG BÁO / BANNER */}
                            {/* ===================================== */}
                            <Card sx={{ mb: 4, backgroundColor: '#FFFFFF' }}>
                                <CardContent sx={{ p: 4 }}>
                                    <Typography variant="subtitle1" fontWeight="800" color="#181C32" mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CampaignOutlined color="primary" /> TẠO MỚI (BANNER / KHUYẾN MÃI)
                                    </Typography>
                                    
                                    <form onSubmit={handleSubmit}>
                                        <Grid container spacing={3} mb={3}>
                                            
                                            <Grid item xs={12} sm={3}>
                                                <Typography variant="caption" fontWeight="800" color="#7E8299" mb={1} display="block">VỊ TRÍ HIỂN THỊ</Typography>
                                                <TextField 
                                                    select 
                                                    fullWidth 
                                                    name="displayLocation" 
                                                    value={displayLocation} 
                                                    onChange={(e) => setDisplayLocation(e.target.value)} 
                                                    required 
                                                    sx={textFieldSx} 
                                                    SelectProps={{ native: true }}
                                                >
                                                    <option value="promo">Thông báo Khuyến mãi</option>
                                                    <option value="home">Banner Trang chủ</option>
                                                </TextField>
                                            </Grid>

                                            <Grid item xs={12} sm={2}>
                                                <Typography variant="caption" fontWeight="800" color="#7E8299" mb={1} display="block">STT</Typography>
                                                <TextField fullWidth name="stt" type="number" required placeholder="Nhập số..." sx={textFieldSx} />
                                            </Grid>
                                            
                                            <Grid item xs={12} sm={7}>
                                                <Typography variant="caption" fontWeight="800" color="#7E8299" mb={1} display="block">LINK ẢNH ĐẠI DIỆN ({t("url_image")})</Typography>
                                                <TextField fullWidth name="image" required placeholder="Nhập đường dẫn ảnh..." sx={textFieldSx} />
                                            </Grid>

                                            {displayLocation === 'promo' && (
                                                <>
                                                    <Grid item xs={12} sm={4}>
                                                        <Typography variant="caption" fontWeight="800" color="#7E8299" mb={1} display="block">DANH MỤC KHUYẾN MÃI</Typography>
                                                        <TextField select fullWidth name="category" defaultValue="" required sx={textFieldSx} SelectProps={{ native: true }}>
                                                            <option value="" disabled>-- Chọn danh mục --</option>
                                                            {categories.map((cat) => (
                                                                <option key={cat._id} value={cat.name}>{cat.name}</option>
                                                            ))}
                                                        </TextField>
                                                    </Grid>

                                                    <Grid item xs={12} sm={8}>
                                                        <Typography variant="caption" fontWeight="800" color="#7E8299" mb={1} display="block">TIÊU ĐỀ ({t("title")})</Typography>
                                                        <TextField fullWidth name="title" required placeholder="Nhập tiêu đề..." sx={textFieldSx} />
                                                    </Grid>

                                                    <Grid item xs={12}>
                                                        <Typography variant="caption" fontWeight="800" color="#7E8299" mb={1} display="block">NỘI DUNG THÔNG BÁO ({t("content_notify")})</Typography>
                                                        <ReactQuill value={content} onChange={handleChangeContent} theme="snow" modules={modules} formats={formats} ref={reactQuillRef} />
                                                    </Grid>
                                                </>
                                            )}

                                        </Grid>

                                        <Box textAlign="right">
                                            <Button type="submit" variant="contained" color="primary" size="large" sx={{ fontWeight: 800, borderRadius: '8px', px: 5, boxShadow: 'none' }}>
                                                {t("add_new")}
                                            </Button>
                                        </Box>
                                    </form>
                                </CardContent>
                            </Card>

                            {/* BẢNG DANH SÁCH - CÓ THÊM TABS ĐỂ LỌC */}
                            <Card sx={{ backgroundColor: '#FFFFFF' }}>
                                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}>
                                    <Tabs 
                                        value={activeTab} 
                                        onChange={(e, newValue) => setActiveTab(newValue)} 
                                        sx={{
                                            '& .MuiTab-root': { fontWeight: 800, fontSize: '0.9rem', color: '#A1A5B7' },
                                            '& .Mui-selected': { color: '#1B84FF !important' },
                                            '& .MuiTabs-indicator': { backgroundColor: '#1B84FF', height: '3px', borderRadius: '3px 3px 0 0' }
                                        }}
                                    >
                                        <Tab label="Thông báo Khuyến mãi" value="promo" />
                                        <Tab label="Banner Trang chủ" value="home" />
                                    </Tabs>
                                </Box>

                                <TableContainer sx={{ overflowX: 'auto' }}>
                                    <Table sx={{ minWidth: 1400 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{...tableHeadSx, pl: 3, width: '60px'}}>STT</TableCell>
                                                <TableCell sx={tableHeadSx}>VỊ TRÍ</TableCell>
                                                <TableCell sx={tableHeadSx}>DANH MỤC</TableCell>
                                                <TableCell sx={tableHeadSx}>{t("title")}</TableCell>
                                                <TableCell sx={tableHeadSx}>{t("url_image")}</TableCell>
                                                <TableCell align="right" sx={{...tableHeadSx, pr: 3}}>{t("more")}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredNotify.length > 0 ? (
                                                filteredNotify.map((item, index) => {
                                                    return (
                                                        <TableRow key={item._id} hover sx={{ verticalAlign: 'middle' }}>
                                                            <TableCell sx={{ pl: 3 }}>
                                                                <Typography variant="body2" fontWeight="800" color="#181C32">{item.stt != null ? item.stt : "-"}</Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                {item.displayLocation === 'home' ? (
                                                                    <Chip label="Trang chủ" size="small" sx={{ fontWeight: 700, borderRadius: '4px', backgroundColor: '#E8FFF3', color: '#50CD89' }} />
                                                                ) : (
                                                                    <Chip label="Khuyến mãi" size="small" sx={{ fontWeight: 700, borderRadius: '4px', backgroundColor: '#F1FAFF', color: '#1B84FF' }} />
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {item.displayLocation === 'home' ? '-' : (
                                                                    <Typography variant="body2" color="text.secondary">{item.category || "Chưa phân loại"}</Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {item.title === "popup" ? (
                                                                    <Chip label={t("popup")} size="small" color="warning" sx={{ fontWeight: 800, borderRadius: '4px' }} />
                                                                ) : (
                                                                    <Typography variant="body2" fontWeight="800" color="#181C32">{item.displayLocation === 'home' ? '[Banner Ảnh]' : item.title}</Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {item.image ? (
                                                                    <img alt="Thumbnail" src={item.image} style={{ width: "80px", height: "auto", borderRadius: '6px', border: '1px solid #E4E6EF' }} />
                                                                ) : <Typography variant="caption" color="text.secondary">Không có ảnh</Typography>}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ pr: 3 }}>
                                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                                    <Button variant="outlined" size="small" 
                                                                        onClick={() => {
                                                                            setLoad(true);
                                                                            axios.post(`${process.env.REACT_APP_API_URL}/auth/editlistnotify`, { id: item._id, isShow: !item.isShow })
                                                                                .then(() => setLoad(true)).catch(() => setLoad(false));
                                                                        }}
                                                                        sx={{ fontWeight: 700, borderRadius: '6px', py: 0.5, color: item.isShow ? '#16A34A' : '#7E8299', borderColor: item.isShow ? '#16A34A' : '#E4E6EF', backgroundColor: item.isShow ? '#DCFCE7' : '#F5F8FA' }}
                                                                        startIcon={item.isShow ? <VisibilityOutlined fontSize="small"/> : <VisibilityOffOutlined fontSize="small"/>}
                                                                    >{item.isShow ? t("hide") : t("show")}</Button>

                                                                    <IconButton size="small" 
                                                                        onClick={() => {
                                                                            axios.get(`${process.env.REACT_APP_API_URL}/auth/notifyall/${item._id}`)
                                                                                .then((res) => { 
                                                                                    setNotifyItem(res.data.data); 
                                                                                    setContent(res.data.data.content); 
                                                                                    setSelectedEditCategory(res.data.data.category || ""); 
                                                                                    setEditDisplayLocation(res.data.data.displayLocation || "promo");
                                                                                    setShow(true); 
                                                                                })
                                                                                .catch(() => swal(t("error"), "", "error"));
                                                                        }}
                                                                        sx={{ color: '#1B84FF', backgroundColor: '#F1FAFF', borderRadius: '6px', '&:hover': { backgroundColor: '#1B84FF', color: '#FFF' } }}
                                                                    ><EditOutlined fontSize="small" /></IconButton>

                                                                    {item.title !== 'popup' && (
                                                                        <IconButton size="small" onClick={() => handleDelete(item._id)} sx={{ color: '#F1416C', backgroundColor: '#FFF5F8', borderRadius: '6px', '&:hover': { backgroundColor: '#F1416C', color: '#FFF' } }}>
                                                                            <DeleteOutline fontSize="small" />
                                                                        </IconButton>
                                                                    )}
                                                                </Stack>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })
                                            ) : (
                                                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, color: '#A1A5B7', fontWeight: 700 }}>Chưa có dữ liệu cho mục này.</TableCell></TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Card>
                        </Container>
                    </Box>

                    {/* ===================================== */}
                    {/* MODAL SỬA THÔNG BÁO / BANNER */}
                    {/* ===================================== */}
                    {isShow && notifyItem != null && (
                        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(3px)' }}>
                            <Card sx={{ width: '90%', maxWidth: '1000px', p: 3, maxHeight: '90vh', overflowY: 'auto' }}>
                                <Typography variant="h6" fontWeight="800" mb={1} color="#181C32">{t("edit")} Dữ liệu</Typography>
                                <Typography variant="body2" color="text.secondary" mb={3}>
                                    Vị trí: <Typography component="span" fontWeight="800" color="#1B84FF">{editDisplayLocation === 'home' ? 'Trang Chủ' : 'Khuyến Mãi'}</Typography>
                                </Typography>
                                
                                <form onSubmit={handleSubmitnotify}>
                                    <Stack spacing={2.5}>
                                        {notifyItem.title !== "marquee" && (
                                            <>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={3}>
                                                        <Typography variant="caption" fontWeight="800" color="text.secondary" mb={1} display="block">VỊ TRÍ HIỂN THỊ</Typography>
                                                        <TextField select fullWidth name="displayLocation" value={editDisplayLocation} onChange={(e) => setEditDisplayLocation(e.target.value)} required sx={textFieldSx} SelectProps={{ native: true }}>
                                                            <option value="promo">Thông báo Khuyến mãi</option>
                                                            <option value="home">Banner Trang chủ</option>
                                                        </TextField>
                                                    </Grid>

                                                    <Grid item xs={4} sm={2}>
                                                        <Typography variant="caption" fontWeight="800" color="text.secondary" mb={1} display="block">STT</Typography>
                                                        <TextField name="stt" defaultValue={notifyItem.stt} type="number" required sx={textFieldSx} fullWidth />
                                                    </Grid>
                                                    
                                                    <Grid item xs={12} sm={7}>
                                                        <Typography variant="caption" fontWeight="800" color="text.secondary" mb={1} display="block">LINK ẢNH ĐẠI DIỆN</Typography>
                                                        <TextField name="image" defaultValue={notifyItem.image} required sx={textFieldSx} fullWidth />
                                                    </Grid>

                                                    {editDisplayLocation === 'promo' && (
                                                        <>
                                                            <Grid item xs={12} sm={4}>
                                                                <Typography variant="caption" fontWeight="800" color="text.secondary" mb={1} display="block">DANH MỤC</Typography>
                                                                <TextField select fullWidth name="category" value={selectedEditCategory} onChange={(e) => setSelectedEditCategory(e.target.value)} required sx={textFieldSx} SelectProps={{ native: true }}>
                                                                    <option value="" disabled>-- Chọn danh mục --</option>
                                                                    {categories.map((cat) => (
                                                                        <option key={cat._id} value={cat.name}>{cat.name}</option>
                                                                    ))}
                                                                </TextField>
                                                            </Grid>
                                                            <Grid item xs={12} sm={8}>
                                                                <Typography variant="caption" fontWeight="800" color="text.secondary" mb={1} display="block">TIÊU ĐỀ</Typography>
                                                                <TextField name="title" defaultValue={notifyItem.title} required sx={textFieldSx} fullWidth />
                                                            </Grid>
                                                        </>
                                                    )}
                                                </Grid>
                                            </>
                                        )}
                                        
                                        {editDisplayLocation === 'promo' && notifyItem.title !== "marquee" && (
                                            <Box>
                                                <Typography variant="caption" fontWeight="800" color="text.secondary" mb={1} display="block">NỘI DUNG</Typography>
                                                <ReactQuill value={content} onChange={handleChangeContent} theme="snow" modules={modules} formats={formats} />
                                            </Box>
                                        )}
                                        
                                        {notifyItem.title === "marquee" && (
                                            <Box>
                                                <Typography variant="caption" fontWeight="800" color="text.secondary" mb={1} display="block">NỘI DUNG CHỮ CHẠY</Typography>
                                                <ReactQuill value={content} onChange={handleChangeContent} theme="snow" modules={modules} formats={formats} />
                                            </Box>
                                        )}

                                        <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
                                            <Button variant="outlined" color="inherit" onClick={() => setShow(false)} sx={{ fontWeight: 800, px: 3, borderRadius: '8px' }}>{t("close")}</Button>
                                            <Button variant="contained" color="primary" type="submit" sx={{ fontWeight: 800, px: 4, borderRadius: '8px', boxShadow: 'none' }}>LƯU THAY ĐỔI</Button>
                                        </Stack>
                                    </Stack>
                                </form>
                            </Card>
                        </Box>
                    )}

                    {/* MODAL THÊM DANH MỤC */}
                    {showAddCatModal && (
                        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(3px)' }}>
                            <Card sx={{ width: '90%', maxWidth: '400px', p: 3, boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
                                <Typography variant="h6" fontWeight="800" mb={3} color="#181C32">Tạo Danh Mục Mới</Typography>
                                <form onSubmit={handleAddCategory}>
                                    <Box mb={2}>
                                        <Typography variant="caption" fontWeight="800" color="text.secondary" mb={1} display="block">TÊN DANH MỤC</Typography>
                                        <TextField name="catName" fullWidth required placeholder="VD: Khuyến mãi..." sx={textFieldSx} autoFocus />
                                    </Box>
                                    <Box mb={4}>
                                        <Typography variant="caption" fontWeight="800" color="text.secondary" mb={1} display="block">SỐ THỨ TỰ</Typography>
                                        <TextField name="catStt" type="number" defaultValue={0} fullWidth required sx={textFieldSx} />
                                    </Box>
                                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                                        <Button variant="outlined" color="inherit" onClick={() => setShowAddCatModal(false)} sx={{ fontWeight: 800, px: 3, borderRadius: '8px' }}>Hủy</Button>
                                        <Button variant="contained" color="primary" type="submit" sx={{ fontWeight: 800, px: 4, borderRadius: '8px', boxShadow: 'none' }}>Thêm</Button>
                                    </Stack>
                                </form>
                            </Card>
                        </Box>
                    )}

                    {/* MODAL SỬA DANH MỤC */}
                    {showEditCatModal && (
                        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(3px)' }}>
                            <Card sx={{ width: '90%', maxWidth: '400px', p: 3, boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
                                <Typography variant="h6" fontWeight="800" mb={3} color="#181C32">Sửa Danh Mục</Typography>
                                <form onSubmit={handleEditCategorySubmit}>
                                    <Box mb={2}>
                                        <Typography variant="caption" fontWeight="800" color="text.secondary" mb={1} display="block">TÊN DANH MỤC</Typography>
                                        <TextField name="catName" defaultValue={editCatData.name} fullWidth required sx={textFieldSx} autoFocus />
                                    </Box>
                                    <Box mb={4}>
                                        <Typography variant="caption" fontWeight="800" color="text.secondary" mb={1} display="block">SỐ THỨ TỰ</Typography>
                                        <TextField name="catStt" type="number" defaultValue={editCatData.stt} fullWidth required sx={textFieldSx} />
                                    </Box>
                                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                                        <Button variant="outlined" color="inherit" onClick={() => setShowEditCatModal(false)} sx={{ fontWeight: 800, px: 3, borderRadius: '8px' }}>Hủy</Button>
                                        <Button variant="contained" color="primary" type="submit" sx={{ fontWeight: 800, px: 4, borderRadius: '8px', boxShadow: 'none' }}>Lưu</Button>
                                    </Stack>
                                </form>
                            </Card>
                        </Box>
                    )}

                </ThemeProvider>
            </DashboardLayout>
        </ThemeProvider>
    );
}

export default SettingNotify;